import React, { useMemo, useState } from 'react';
import { Alert } from 'antd';
import QueryBuilder from '../QueryBuilder/QueryBuilder';
import CombinationSelector from '../CombinationSelector';
import LogicSummary from './LogicSummary';
import {
  findEmptyBaselineRiskRules,
  emptyBaselineWarningMessage
} from './linearRegressionBaselineWarning';

const formulaInfoTooltip = (
  <span>
    Final decision. Combines function results with event fields and arithmetic —
    when this evaluates to true, the rule triggers and <code>matchAction</code>{' '}
    (STOP / REJECT / ALLOW) fires.
  </span>
);

const Formula = ({
  variantInfo = null,
  typoIndex,
  variantIndex,
  setVariantRenderData,
  onDirtyField,
  isReadOnly,
  disableActions,
  generationState,
  thresholdResults
}) => {
  const [humanString, setHumanString] = useState('');

  const ruleResults = useMemo(() => {
    if (
      generationState !== 'generated' ||
      !thresholdResults?.rules ||
      !variantInfo
    ) {
      return null;
    }
    const ruleId = variantInfo.ruleId || variantInfo.variantId;
    return thresholdResults.rules.find((r) => r.ruleId === ruleId) || null;
  }, [generationState, thresholdResults, variantInfo]);

  const showThresholdUI = generationState === 'generated' && ruleResults;

  // FIN-12216: warn when a linear-regression threshold with bias = 0 has no baseline > 0 guard —
  // such a rule fires on any value for an entity with no history (threshold collapses to 0).
  const baselineWarning = useMemo(
    () =>
      emptyBaselineWarningMessage(
        findEmptyBaselineRiskRules(
          variantInfo && variantInfo.functionAggregation
        )
      ),
    [variantInfo]
  );

  return (
    <div className="formula">
      {/* FIN-12167 — LOGIC SUMMARY + rule config (QueryBuilder) at the TOP; the recommended/
          generated threshold boxes moved to the BOTTOM as reference, below the config the user
          is actually editing. */}
      {/* FIN-12216 — empty-baseline (linear-regression, bias=0) warning stays at the top. */}
      {baselineWarning && (
        <Alert
          type="warning"
          showIcon
          message={baselineWarning}
          style={{ marginBottom: 16 }}
        />
      )}
      <LogicSummary humanString={humanString} />
      <QueryBuilder
        type={`functionAggregation`}
        itemNoun="formula"
        setVariantRenderData={setVariantRenderData}
        typoIndex={typoIndex}
        variantInfo={variantInfo}
        variantIndex={variantIndex}
        isReadOnly={isReadOnly}
        allowNewIfEmpty={true}
        onDirtyField={onDirtyField}
        queryValue={variantInfo.functionAggregation}
        isFieldWithFunction={true}
        disableActions={disableActions}
        generationState={generationState}
        onHumanStringChange={setHumanString}
        headerInfo={formulaInfoTooltip}
      />
      {showThresholdUI && (
        <>
          <Alert
            type="info"
            showIcon
            message="Thresholds are generated. Use the recommended values below as reference when setting threshold values."
            style={{ marginTop: 16, marginBottom: 16 }}
          />
          <CombinationSelector
            combinations={ruleResults.combinations}
            targetAlertRate={ruleResults.targetAlertRate}
            currentThresholds={ruleResults.currentThresholds}
            currentFlaggedCount={ruleResults.currentFlaggedCount}
            totalAfterFixedFilter={ruleResults.totalAfterFixedFilter}
          />
        </>
      )}
    </div>
  );
};

export default Formula;
